import { useState, useCallback } from 'react';
import AzureAIService from '../../../../../services/azureAIService';
import WizardSubmissionService from '../../../../../services/wizardSubmissionService';
import { normalizeCvData } from '../utils/normalizers';

/**
 * Custom hook for managing AI Sales Playbook generation and translation
 */
export const useSalesPlaybook = (submission, scoringResults) => {
    const [salesPlaybook, setSalesPlaybook] = useState(null);
    const [loadingPlaybook, setLoadingPlaybook] = useState(false);
    const [generatingPlaybook, setGeneratingPlaybook] = useState(false);
    const [reanalyzingCV, setReanalyzingCV] = useState(false);

    // Language states
    const [playbookLanguage, setPlaybookLanguage] = useState('english');
    const [tamilPlaybook, setTamilPlaybook] = useState(null);
    const [translatingPlaybook, setTranslatingPlaybook] = useState(false);

    // Load existing playbook from submission data
    const loadPlaybook = useCallback(async (submissionData) => {
        try {
            setLoadingPlaybook(true);

            // Load existing playbook if stored
            if (submissionData.cv_data?.salesPlaybook) {
                setSalesPlaybook(submissionData.cv_data.salesPlaybook);
                console.log('📋 Loaded existing playbook from database');
            }

            // Load Tamil playbook if exists
            if (submissionData.cv_data?.tamilPlaybook) {
                setTamilPlaybook(submissionData.cv_data.tamilPlaybook);
                console.log('🌐 Loaded existing Tamil playbook from database');
            }
        } catch (error) {
            console.error('Error loading sales playbook:', error);
        } finally {
            setLoadingPlaybook(false);
        }
    }, []);

    // Analyze CV from URL
    const analyzeCVFromURL = useCallback(async (cvData, submissionData) => {
        console.log('🔍 Analyzing CV from URL:', cvData.publicUrl);

        const response = await fetch(cvData.publicUrl);
        if (!response.ok) {
            throw new Error('Failed to fetch CV file');
        }

        const blob = await response.blob();
        const file = new File([blob], cvData.fileName, { type: cvData.fileType });

        const azureAI = new AzureAIService();

        console.log('📄 Extracting CV information...');
        const extractedData = await azureAI.extractCVInformation(file, cvData.publicUrl);
        console.log('✅ CV extracted:', extractedData);

        console.log('🧠 Analyzing CV for immigration...');
        const userProfile = {
            personalInfo: {
                firstName: submissionData.first_name,
                lastName: submissionData.last_name
            },
            countrySelection: submissionData.country_selection,
            education: submissionData.education_info,
            experience: submissionData.experience_info,
            languageSkills: submissionData.language_info,
            financialInfo: submissionData.financial_info
        };

        const aiAnalysis = await azureAI.analyzeCVForImmigration(userProfile, extractedData);
        console.log('✅ CV analyzed:', aiAnalysis);

        return {
            ...cvData,
            extractedData,
            aiAnalysis,
            analyzedAt: new Date().toISOString()
        };
    }, []);

    // Force re-analyze CV
    const forceReanalyzeCv = useCallback(async (submissionData, updateSubmission) => {
        try {
            setReanalyzingCV(true);
            console.log('🔄 Force re-analyzing CV with enhanced prompt...');

            if (!submissionData.cv_data?.publicUrl) {
                throw new Error('No CV file found to analyze');
            }

            const updatedCvData = await analyzeCVFromURL(submissionData.cv_data, submissionData);
            console.log('✅ Fresh CV analysis completed:', updatedCvData);

            // Update via service
            await WizardSubmissionService.updateCVAnalysis(submissionData.id, updatedCvData);

            if (updateSubmission) {
                updateSubmission(prev => prev ? {
                    ...prev,
                    cv_data: normalizeCvData(updatedCvData)
                } : prev);
            }

            console.log('💾 New CV analysis saved to database');
            return updatedCvData;
        } catch (error) {
            console.error('❌ Error re-analyzing CV:', error);
            throw error;
        } finally {
            setReanalyzingCV(false);
        }
    }, [analyzeCVFromURL]);

    // Translate playbook to Tamil
    const translateToTamil = useCallback(async (submissionData, forceRetranslate = false) => {
        try {
            setTranslatingPlaybook(true);
            console.log('🌐 Translating playbook to Tamil...');

            if (!salesPlaybook) {
                throw new Error('No playbook to translate');
            }

            // Check if translation already exists
            if (tamilPlaybook && !forceRetranslate) {
                console.log('ℹ️ Tamil translation already exists');
                setPlaybookLanguage('tamil');
                return tamilPlaybook;
            }

            const azureAI = new AzureAIService();

            const translationPromise = azureAI.translatePlaybookToTamil(salesPlaybook);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Translation timeout after 1 minute')), 60000)
            );

            const translated = await Promise.race([translationPromise, timeoutPromise]);

            setTamilPlaybook(translated);
            setPlaybookLanguage('tamil');

            // Save to database
            try {
                const updatedCVData = {
                    ...(submissionData.cv_data || {}),
                    tamilPlaybook: translated,
                    tamilPlaybookGeneratedAt: new Date().toISOString()
                };

                await WizardSubmissionService.updateCVAnalysis(submissionData.id, updatedCVData);
                console.log('✅ Tamil translation saved to database');
            } catch (saveError) {
                console.error('⚠️ Failed to save Tamil translation:', saveError);
            }

            return translated;
        } catch (error) {
            console.error('❌ Error translating playbook:', error);
            throw error;
        } finally {
            setTranslatingPlaybook(false);
        }
    }, [salesPlaybook, tamilPlaybook]);

    // Get current playbook based on language
    const getCurrentPlaybook = useCallback(() => {
        return playbookLanguage === 'tamil' && tamilPlaybook ? tamilPlaybook : salesPlaybook;
    }, [playbookLanguage, tamilPlaybook, salesPlaybook]);

    return {
        // State
        salesPlaybook,
        tamilPlaybook,
        playbookLanguage,
        loadingPlaybook,
        generatingPlaybook,
        translatingPlaybook,
        reanalyzingCV,

        // Actions
        setSalesPlaybook,
        setPlaybookLanguage,
        setGeneratingPlaybook,
        loadPlaybook,
        analyzeCVFromURL,
        forceReanalyzeCv,
        translateToTamil,

        // Utilities
        getCurrentPlaybook
    };
};

export default useSalesPlaybook;
